import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { PlatformMessagesService } from '../../services/platform-messages.service';
import { SocketService } from 'src/app/shared/services/socket.service';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import Swal from 'sweetalert2';
import { tieneSaldoVes } from 'src/app/shared/utils/saldo-ves';

/**
 * `wallet_ves`: clientes con wallet VES (target `clients_wallet_ves` de Nest):
 * familias VE y CL completas más quien tenga movimientos VES con cédula V/E.
 */
type AudienceTab = 'operators' | 've' | 'cl' | 'wallet_ves' | 'all_clients';

interface Recipient {
  userId: number;
  cognitoId: string;
  email: string;
  nombre: string;
  apellidos: string;
  tipoId: string;
  isOperator: boolean;
  online: boolean;
}

@Component({
  selector: 'app-platform-messages-send',
  templateUrl: './platform-messages-send.component.html',
  styleUrls: ['./platform-messages-send.component.sass'],
})
export class PlatformMessagesSendComponent implements OnInit, OnDestroy {
  private subs = new Subscription();
  form!: FormGroup;

  mainTab: 'send' | 'history' = 'send';

  // Audience
  audienceTab: AudienceTab = 'operators';
  audienceTabs: { key: AudienceTab; label: string; icon: string }[] = [
    { key: 'operators', label: 'Operadores', icon: 'admin_panel_settings' },
    { key: 've', label: 'Venezuela', icon: 'flag' },
    { key: 'cl', label: 'Chile', icon: 'flag' },
    { key: 'wallet_ves', label: 'Clientes con wallet VES', icon: 'account_balance_wallet' },
    { key: 'all_clients', label: 'Todos los clientes', icon: 'groups' },
  ];

  // Data
  private allRecipients: Recipient[] = [];
  onlineRecipients: Recipient[] = [];
  offlineRecipients: Recipient[] = [];
  searchValue = '';
  loadingRecipients = false;
  socketConnected = false;

  // Selection — fuente única de verdad
  private selection = new Set<string>();

  // Historial
  private allSentMessages: any[] = [];
  sentMessages: any[] = [];
  sentPage = 1;
  sentTotalPages = 0;
  expandedBatchId: string | null = null;
  historyFilter: 'all' | 'operators' | 've' | 'cl' | 'wallet_ves' | 'clients' = 'all';
  historyFilterTabs: { key: string; label: string }[] = [
    { key: 'all', label: 'Todos' },
    { key: 'operators', label: 'Operadores' },
    { key: 've', label: 'Venezuela' },
    { key: 'cl', label: 'Chile' },
    { key: 'wallet_ves', label: 'Wallet VES' },
    { key: 'clients', label: 'Clientes' },
  ];

  constructor(
    private fb: FormBuilder,
    private messagesService: PlatformMessagesService,
    private socketService: SocketService,
    private spinner: SpinnerService,
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      subject: ['', [Validators.required, Validators.maxLength(255)]],
      body: ['', [Validators.required, Validators.maxLength(5000)]],
    });

    this.loadAllRecipients();

    this.subs.add(
      this.socketService.isConnected$().subscribe((v) => (this.socketConnected = v)),
    );
    this.subs.add(
      this.socketService.onUsersUpdated$().subscribe((data) =>
        this.updateOnlineStatus(data),
      ),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  // ===================== DATA =====================

  private async loadAllRecipients(): Promise<void> {
    this.loadingRecipients = true;
    try {
      const [clientsRes, operatorsRes] = await Promise.all([
        this.messagesService.getRecipients({ type: 'client', limit: 5000 }),
        this.messagesService.getRecipients({ type: 'operator', limit: 5000 }),
      ]);
      this.allRecipients = [
        ...(operatorsRes.data || []),
        ...(clientsRes.data || []),
      ];
      this.applyFilter();
    } catch (err) {
      console.error('Error loading recipients', err);
    } finally {
      this.loadingRecipients = false;
    }
  }

  private updateOnlineStatus(data: any): void {
    if (!data?.users) return;
    const onlineIds = new Set<string>(data.users.map((u: any) => u.cognitoId));
    this.allRecipients.forEach((r) => (r.online = onlineIds.has(r.cognitoId)));
    this.applyFilter();
  }

  // ===================== FILTER =====================

  setAudienceTab(tab: AudienceTab): void {
    this.audienceTab = tab;
    this.searchValue = '';
    this.selection.clear();
    this.applyFilter();
  }

  onSearchInput(event: Event): void {
    this.searchValue = (event.target as HTMLInputElement).value.toLowerCase();
    this.applyFilter();
  }

  private applyFilter(): void {
    let filtered = this.allRecipients;

    switch (this.audienceTab) {
      case 'operators':
        filtered = filtered.filter((r) => r.isOperator);
        break;
      case 've':
        filtered = filtered.filter((r) => !r.isOperator && r.tipoId === 'VE');
        break;
      case 'cl':
        filtered = filtered.filter((r) => !r.isOperator && r.tipoId !== 'VE');
        break;
      case 'wallet_ves':
        // Aproximación por tipo de documento (familias VE y CL). Al enviar a
        // todos manda el back (`clients_wallet_ves`), que además suma a quien
        // tenga movimientos VES con cédula V/E aunque no tenga tipo.
        filtered = filtered.filter((r) => !r.isOperator && tieneSaldoVes(r.tipoId));
        break;
      case 'all_clients':
        filtered = filtered.filter((r) => !r.isOperator);
        break;
    }

    if (this.searchValue) {
      filtered = filtered.filter(
        (r) =>
          r.nombre?.toLowerCase().includes(this.searchValue) ||
          r.apellidos?.toLowerCase().includes(this.searchValue) ||
          r.email?.toLowerCase().includes(this.searchValue) ||
          String(r.userId).includes(this.searchValue),
      );
    }

    this.onlineRecipients = filtered.filter((r) => r.online);
    this.offlineRecipients = filtered.filter((r) => !r.online);
  }

  // ===================== SELECTION =====================

  isSelected(r: Recipient): boolean {
    return this.selection.has(r.cognitoId);
  }

  toggleRecipient(r: Recipient): void {
    if (this.selection.has(r.cognitoId)) {
      this.selection.delete(r.cognitoId);
    } else {
      this.selection.add(r.cognitoId);
    }
  }

  selectAllOnline(): void {
    this.onlineRecipients.forEach((r) => this.selection.add(r.cognitoId));
  }

  selectAllVisible(): void {
    this.onlineRecipients.forEach((r) => this.selection.add(r.cognitoId));
    this.offlineRecipients.forEach((r) => this.selection.add(r.cognitoId));
  }

  deselectAll(): void {
    this.selection.clear();
  }

  get selectedCount(): number {
    return this.selection.size;
  }

  get filteredTotal(): number {
    return this.onlineRecipients.length + this.offlineRecipients.length;
  }

  get audienceLabel(): string {
    return this.audienceTabs.find((t) => t.key === this.audienceTab)?.label || '';
  }

  // ===================== SEND =====================

  private get targetTypeForAll(): string {
    switch (this.audienceTab) {
      case 'operators':
        return 'all_operators';
      case 've':
        return 'clients_ve';
      case 'cl':
        return 'clients_cl';
      case 'wallet_ves':
        return 'clients_wallet_ves';
      case 'all_clients':
        return 'all_clients';
    }
  }

  private get targetTypeForSelected(): string {
    switch (this.audienceTab) {
      case 'operators':
        return 'selected_operators';
      case 've':
      case 'cl':
      case 'wallet_ves':
      case 'all_clients':
        return 'selected_clients';
    }
  }

  async onSend(): Promise<void> {
    if (this.form.invalid) return;

    const { subject, body } = this.form.value;
    const ids = Array.from(this.selection);
    const isAll = ids.length === 0;
    const destLabel = isAll
      ? `Todos los ${this.audienceLabel}`
      : `${ids.length} destinatario(s) seleccionado(s)`;
    const bodyPreview =
      body.length > 150 ? body.substring(0, 150) + '...' : body;

    const confirm = await Swal.fire({
      iconHtml:
        '<img src="assets/icons/question.svg" style="width: 80px; height: 80px; margin-top:20px"/>',
      customClass: { icon: 'no-border animate-bounce' },
      title: 'Confirmar envío',
      html: `
        <div style="text-align: left; font-size: 0.85rem; color: #374151;">
          <div style="background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 10px; padding: 12px; margin-bottom: 12px;">
            <div style="font-size: 0.7rem; color: #6b7280; font-weight: 600; text-transform: uppercase; margin-bottom: 4px;">Destinatarios</div>
            <div style="font-weight: 700; color: #1C593D;">${destLabel}</div>
          </div>
          <div style="background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px; margin-bottom: 12px;">
            <div style="font-size: 0.7rem; color: #6b7280; font-weight: 600; text-transform: uppercase; margin-bottom: 4px;">Asunto</div>
            <div style="font-weight: 600;">${subject}</div>
          </div>
          <div style="background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px;">
            <div style="font-size: 0.7rem; color: #6b7280; font-weight: 600; text-transform: uppercase; margin-bottom: 4px;">Mensaje</div>
            <div style="white-space: pre-wrap; font-size: 0.82rem;">${bodyPreview}</div>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Enviar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1C593D',
      reverseButtons: true,
    });

    if (!confirm.isConfirmed) return;

    const payload: any = { subject, body };

    if (isAll) {
      payload.targetType = this.targetTypeForAll;
    } else {
      payload.targetType = this.targetTypeForSelected;
      payload.recipientCognitoIds = ids;
    }

    const ref = this.spinner.start();
    try {
      const res: any = await this.messagesService.send(payload);
      Swal.fire({
        icon: 'success',
        title: 'Mensaje enviado',
        text: `Se envió a ${res.totalRecipients} destinatario(s)`,
        confirmButtonColor: '#1C593D',
      });
      this.form.patchValue({ subject: '', body: '' });
      this.selection.clear();
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err?.error?.message || 'No se pudo enviar el mensaje',
        confirmButtonColor: '#1C593D',
      });
    } finally {
      this.spinner.stop(ref);
    }
  }

  // ===================== HISTORY =====================

  setMainTab(tab: 'send' | 'history'): void {
    this.mainTab = tab;
    if (tab === 'history') this.loadSent();
  }

  async loadSent(): Promise<void> {
    const ref = this.spinner.start();
    try {
      const res = await this.messagesService.getSent(this.sentPage, 100);
      this.allSentMessages = res.data;
      this.sentTotalPages = res.meta.totalPages;
      this.applyHistoryFilter();
    } catch (err) {
      console.error('Error loading sent', err);
    } finally {
      this.spinner.stop(ref);
    }
  }

  setHistoryFilter(filter: string): void {
    this.historyFilter = filter as any;
    this.applyHistoryFilter();
  }

  private applyHistoryFilter(): void {
    if (this.historyFilter === 'all') {
      this.sentMessages = [...this.allSentMessages];
      return;
    }
    this.sentMessages = this.allSentMessages.filter((msg) => {
      const t = msg.targetType;
      switch (this.historyFilter) {
        case 'operators':
          return t === 'all_operators' || t === 'selected_operators';
        case 've':
          return t === 'clients_ve';
        case 'cl':
          return t === 'clients_cl';
        case 'wallet_ves':
          return t === 'clients_wallet_ves';
        case 'clients':
          return (
            t === 'all_clients' ||
            t === 'selected_clients' ||
            t === 'clients_ve' ||
            t === 'clients_cl' ||
            t === 'clients_wallet_ves'
          );
        default:
          return true;
      }
    });
  }

  toggleHistoryCard(batchId: string): void {
    this.expandedBatchId = this.expandedBatchId === batchId ? null : batchId;
  }

  getDeliveryPercent(msg: any): number {
    if (!msg.totalRecipients) return 0;
    return Math.round(
      ((msg.deliveredOnlineCount + msg.emailSentCount) / msg.totalRecipients) * 100,
    );
  }

  getReadPercent(msg: any): number {
    if (!msg.totalRecipients) return 0;
    return Math.round((msg.readCount / msg.totalRecipients) * 100);
  }

  onSentPageChange(delta: number): void {
    const next = this.sentPage + delta;
    if (next < 1 || next > this.sentTotalPages) return;
    this.sentPage = next;
    this.loadSent();
  }

  formatDate(date: string): string {
    return new Date(date).toLocaleString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  getTargetLabel(value: string): string {
    const map: Record<string, string> = {
      all_operators: 'Todos los operadores',
      all_clients: 'Todos los clientes',
      clients_ve: 'Venezuela',
      clients_cl: 'Chile',
      clients_wallet_ves: 'Clientes con wallet VES',
      selected_operators: 'Operadores seleccionados',
      selected_clients: 'Clientes seleccionados',
    };
    return map[value] || value;
  }
}
